// src/pages/TypingProgress.jsx
import React, { useState, useEffect } from 'react';
import { theme } from '../utils/theme';
import { getAllStudents, getTypingLogs } from '../firebase/database';
import { format } from 'date-fns';

const C = theme.colors;

export default function TypingProgress() {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSessions, setUserSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const allStudents = await getAllStudents();
      setStudents(allStudents);
    } catch (err) {
      console.error("Error fetching typing progress:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUser) {
      loadUserSessions(selectedUser.id);
    }
  }, [selectedUser]);

  const loadUserSessions = async (userId) => {
    setLoadingSessions(true);
    setUserSessions([]); // Reset sessions while loading
    try {
      const logs = await getTypingLogs(userId);
      setUserSessions(logs);
    } catch (err) {
      console.error("Error loading user sessions:", err);
    } finally {
      setLoadingSessions(false);
    }
  };

  // Prepare stats using the typingStats summary in the profile
  const userStats = students.map(student => {
    const stats = student.typingStats || { totalSessions: 0, avgWpm: 0, avgAccuracy: 0, lastSession: null };
    
    return {
      ...student,
      totalSessions: stats.totalSessions || 0,
      avgWpm: stats.avgWpm || 0,
      avgAccuracy: stats.avgAccuracy || 0,
      lastSession: stats.lastSession || null
    };
  });

  const filteredStats = userStats.filter(s => {
    const matchesSearch = (s.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (s.roll && s.roll.toString().includes(searchTerm)) ||
                          (s.id && s.id.toString().includes(searchTerm));
    
    const matchesStatus = showInactive ? true : s.status !== "inactive";
    
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    // Sort students with sessions to the top
    if (a.totalSessions > 0 && b.totalSessions === 0) return -1;
    if (a.totalSessions === 0 && b.totalSessions > 0) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px', fontFamily: theme.fonts.sans }}>
      <style>{`
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(2px); padding: 20px; }
        .modal-container { background: #fff; width: 100%; maxWidth: 800px; max-height: 80vh; borderRadius: 20px; overflow: hidden; display: flex; flexDirection: column; box-shadow: 0 20px 50px rgba(0,0,0,0.2); }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: 0 }}>Typing Progress</h1>
          <p style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>Monitor student and staff typing performance</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button 
            onClick={() => setShowInactive(!showInactive)}
            style={{
              padding: '10px 16px',
              borderRadius: 10,
              border: `1px solid ${showInactive ? C.accent : C.border}`,
              background: showInactive ? C.accentSoft : '#fff',
              color: showInactive ? C.accent : C.muted,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: '0.2s'
            }}
          >
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: showInactive ? C.accent : C.dim }} />
            {showInactive ? "Showing Inactive" : "Show Inactive"}
          </button>
          <input 
            type="text" 
            placeholder="Search student or roll no..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ 
              padding: '10px 16px', 
              borderRadius: 10, 
              border: `1px solid ${C.border}`, 
              width: 260,
              fontSize: 14,
              outline: 'none',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 100, color: C.muted }}>Loading data...</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: theme.shadows.soft }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                <th style={{ padding: '16px 20px', fontSize: 12, fontWeight: 800, color: C.muted, textTransform: 'uppercase' }}>User</th>
                <th style={{ padding: '16px 20px', fontSize: 12, fontWeight: 800, color: C.muted, textTransform: 'uppercase' }}>Sessions</th>
                <th style={{ padding: '16px 20px', fontSize: 12, fontWeight: 800, color: C.muted, textTransform: 'uppercase' }}>Avg WPM</th>
                <th style={{ padding: '16px 20px', fontSize: 12, fontWeight: 800, color: C.muted, textTransform: 'uppercase' }}>Avg Accuracy</th>
                <th style={{ padding: '16px 20px', fontSize: 12, fontWeight: 800, color: C.muted, textTransform: 'uppercase' }}>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {filteredStats.length > 0 ? filteredStats.map((stat, idx) => (
                <tr key={stat.id} style={{ borderBottom: idx === filteredStats.length - 1 ? 'none' : `1px solid ${C.border}`, transition: '0.2s', opacity: stat.totalSessions > 0 ? 1 : 0.6 }}>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ fontWeight: 700, color: C.text }}>{stat.name || "Unknown"}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>
                      Roll: {stat.roll || "N/A"} • {stat.role}
                      {stat.status === "inactive" && <span style={{ color: C.red, marginLeft: 6, fontWeight: 700 }}>(Inactive)</span>}
                    </div>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <button 
                      onClick={() => stat.totalSessions > 0 && setSelectedUser(stat)}
                      style={{ 
                        padding: '6px 12px', 
                        borderRadius: 8, 
                        background: stat.totalSessions > 0 ? C.accentSoft : C.bg, 
                        color: stat.totalSessions > 0 ? C.accent : C.muted, 
                        border: 'none', 
                        fontWeight: 800, 
                        fontSize: 13,
                        cursor: stat.totalSessions > 0 ? 'pointer' : 'default',
                        transition: '0.2s'
                      }}
                    >
                      {stat.totalSessions} Sessions
                    </button>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <span style={{ fontWeight: 800, color: C.accent, fontSize: 15 }}>{stat.avgWpm}</span>
                    <span style={{ fontSize: 10, color: C.muted, marginLeft: 4 }}>WPM</span>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 60, height: 6, background: C.bg, borderRadius: 10, overflow: 'hidden' }}>
                        <div style={{ width: `${stat.avgAccuracy}%`, height: '100%', background: stat.avgAccuracy > 90 ? C.green : C.amber }} />
                      </div>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{stat.avgAccuracy}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    {stat.lastSession ? (
                      <div style={{ fontSize: 13 }}>
                        <div style={{ color: C.text }}>{format(new Date(stat.lastSession.timestamp), 'MMM d, h:mm a')}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{stat.lastSession.lessonTitle}</div>
                      </div>
                    ) : '-'}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" style={{ padding: 40, textAlign: 'center', color: C.muted }}>No student profiles found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Detailed Sessions Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div style={{ padding: '24px 30px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{selectedUser.name}'s History</h2>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Total {selectedUser.totalSessions} sessions recorded</div>
              </div>
              <button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: C.muted }}>✕</button>
            </div>
            
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px 30px' }}>
              {loadingSessions ? (
                <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Loading session history...</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.bg}` }}>
                      <th style={{ padding: '12px 10px', fontSize: 11, color: C.muted, textTransform: 'uppercase' }}>Date & Time</th>
                      <th style={{ padding: '12px 10px', fontSize: 11, color: C.muted, textTransform: 'uppercase' }}>Lesson</th>
                      <th style={{ padding: '12px 10px', fontSize: 11, color: C.muted, textTransform: 'uppercase' }}>WPM</th>
                      <th style={{ padding: '12px 10px', fontSize: 11, color: C.muted, textTransform: 'uppercase' }}>Accuracy</th>
                      <th style={{ padding: '12px 10px', fontSize: 11, color: C.muted, textTransform: 'uppercase' }}>Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userSessions.length > 0 ? userSessions.map((session, i) => (
                      <tr key={session.id} style={{ borderBottom: `1px solid ${C.bg}` }}>
                        <td style={{ padding: '14px 10px', fontSize: 13 }}>{format(new Date(session.timestamp), 'MMM d, h:mm a')}</td>
                        <td style={{ padding: '14px 10px', fontSize: 13, fontWeight: 600 }}>{session.lessonTitle}</td>
                        <td style={{ padding: '14px 10px', fontSize: 13, fontWeight: 800, color: C.accent }}>{session.speedWPM}</td>
                        <td style={{ padding: '14px 10px', fontSize: 13 }}>
                          <span style={{ padding: '2px 6px', borderRadius: 4, background: session.accuracy > 90 ? C.green + '15' : C.amber + '15', color: session.accuracy > 90 ? C.green : C.amber, fontWeight: 700 }}>
                            {session.accuracy}%
                          </span>
                        </td>
                        <td style={{ padding: '14px 10px', fontSize: 11, textTransform: 'capitalize' }}>{session.mode}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" style={{ padding: 20, textAlign: 'center', color: C.muted }}>No detailed session logs found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
